import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE, ANCILLARY_SERVICES_MCP_SUFFIX } from '../Constants';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';

export default function AncillaryServicesMCP(props: ChartProps) {
    // ***************************************************************************************************************************************************
    // *** Custom Block: the following two defintions are custom to each table this code can produce,
    //      they provide the location for the data and the names of the columns to display
    const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${ANCILLARY_SERVICES_MCP_SUFFIX}`;
    const download_filename = 'AncillaryServicesMCP';
    const popout_path = '/charts/ancillaryservicesmcp';
    const modalHeader: React.ReactNode = <span>Ancillary Services MCP</span>;

    interface IRow {
        number: number | string;
        GenRegMCP: number | string;
        GenSpinMCP: number | string;
        GenSuppMCP: number | string;
        StrMcp: number | string;
        DemandRegMcp: number | string;
        DemandSpinMcp: number | string;
        DemandSuppMCP: number | string; 
        RcpUpMcp: number | string;
        RcpDownMcp: number | string;
    }

    function extractRowDataAndIntervalString(json: any) {
        const rowData = json.MCPData.RealTimeMCP.Zone ?? [];
        const intervalString = json.MCPData.RefId ?? '';
        const rowDataDayAhead = json.MCPData.DayAheadMCP.Zone ?? [];
        const hourEnding = json.MCPData.DayAheadMCP.HourEnding;
        return { rowData, intervalString, rowDataDayAhead, hourEnding };
    }

    const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
        return (
            <>
                <span>
                    The static, Zonal Regulation Map identifies reserve zones used to calculate market clearing prices for Regulating, Spinning, Supplemental and Short-Term
                    Reserves. See the Real-Time chart for updates.
                </span>
                <br />
                <br /> <span>This table is updated every 5 minutes.</span>
            </>
        );
    }, []);
    // ***************************************************************************************************************************************************

    const [buttonClicked] = [props.buttonClicked];
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
    const [rawData, setRawData] = useState('');

    useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );

        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
                const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
                if (newWinddow) newWinddow.opener = null;
                break;
            default:
                break;
        }
    }, [buttonClicked, rawData, api_url, descriptionBody]);

    // One-time random refresh interval between 4m50s (290000 ms) and 5m10s (310000 ms)
    const REFRESH_MIN_MS = 290_000;
    const REFRESH_MAX_MS = 310_000;
    const refreshIntervalRef = useRef<number>(
        Math.floor(REFRESH_MIN_MS + Math.random() * (REFRESH_MAX_MS - REFRESH_MIN_MS))
    );
    // console.debug('AncillaryServicesMCP refresh interval (ms):', refreshIntervalRef.current);

    const useFetchJson = <T,>(url: string) => {
        const [data, setData] = useState<T[]>();
        const [dataDayAhead, setDataDayAhead] = useState<T[]>();
        const [loading, setLoading] = useState(false);
        const [dataInterval, setDataInterval] = useState<string>('');
        const [dayAheadHourEnding, setDayAheadHourEnding] = useState<string>('');
        const [error, setError] = useState<string>('');

        // Stable refs so hooks dependency list stays minimal
        const numericFieldsRef = useRef<string[]>([
            'number',
            'GenRegMCP',
            'GenSpinMCP',
            'GenSuppMCP',
            'StrMcp',
            'DemandRegMcp',
            'DemandSpinMcp',
            'DemandSuppMCP',
            'RcpUpMcp',
            'RcpDownMcp'
        ]);

        const coerce = useCallback((rows: any[]) => {
            return rows.map(r => {
                const c: any = { ...r };
                if (c.DemandSuppMcp !== undefined && c.DemandSuppMCP === undefined) {
                    c.DemandSuppMCP = c.DemandSuppMcp;
                }
                numericFieldsRef.current.forEach(f => {
                    if (c[f] !== undefined && c[f] !== null && c[f] !== '') {
                        const n = parseFloat(String(c[f]).replace(/,/g, ''));
                        if (!Number.isNaN(n)) c[f] = n;
                    }
                });
                return c;
            });
        }, []);

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            const fetchData = async () => {
                setLoading(true);
                setError('');
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    setRawData(json);
                    const { rowData, intervalString, rowDataDayAhead, hourEnding } = extractRowDataAndIntervalString(json);
                    setDataInterval(intervalString);
                    setData(coerce(rowData));
                    setDataDayAhead(coerce(rowDataDayAhead));
                    setDayAheadHourEnding(hourEnding);
                } catch {
                    setData([]);
                    setDataDayAhead([]);
                    setError('Data Connection Failed');
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
            intervalId = setInterval(fetchData, refreshIntervalRef.current);
            return () => clearInterval(intervalId);
        }, [url, coerce]);

        return { data, loading, dataInterval, dataDayAhead, dayAheadHourEnding, error };
    };

    const { data, loading, dataInterval, dataDayAhead, dayAheadHourEnding, error } = useFetchJson<IRow>(api_url);

    // Controlled sorting (Day-Ahead)
    const [sortDayAhead, setSortDayAhead] = useState<SortDescriptor[]>([]);
    const sortedDayAhead = useMemo(
        () => orderBy(dataDayAhead || [], sortDayAhead),
        [dataDayAhead, sortDayAhead]
    );
    // Controlled sorting (Real-Time)
    const [sortRealTime, setSortRealTime] = useState<SortDescriptor[]>([]);
    const sortedRealTime = useMemo(
        () => orderBy(data || [], sortRealTime),
        [data, sortRealTime]
    );

    if (loading) return <div className='loading-and-error-box'>Loading...</div>;
    if (error) return <div className='loading-and-error-box'>{error}</div>;
    if (!data || data.length === 0) return <div className='loading-and-error-box'>No Data Available</div>;

    return (
        <>
            <VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
            <div className='interval-line'>Hour Ending {dayAheadHourEnding}</div>
            <div className='full-width-grid-wrapper'>
                <Grid
                    data={sortedDayAhead}
                    style={{ width: '100%' }}
                    scrollable="none"
                    resizable
                    sortable={{ allowUnsort: true, mode: 'multiple' }}
                    sort={sortDayAhead}
                    onSortChange={e => setSortDayAhead(e.sort)}
                >
                    <Column field="number" title="Day-Ahead Market Zone" />
                    <Column field="GenRegMCP" title="Generation Regulation" className="k-text-end" format="{0:n2}" />
                    <Column field="GenSpinMCP" title="Generation Spinning Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="GenSuppMCP" title="Generation Supplemental Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="StrMcp" title="Short-Term Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandRegMcp" title="Demand Regulation" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandSpinMcp" title="Demand Spinning Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandSuppMCP" title="Demand Supplemental Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="RcpUpMcp" title="Ramp Capability Up" className="k-text-end" format="{0:n2}" />
                    <Column field="RcpDownMcp" title="Ramp Capability Down" className="k-text-end" format="{0:n2}" />
                </Grid>
            </div>
            <br />
            <div className='interval-line'>{dataInterval}</div>
            <div className='full-width-grid-wrapper'>
                <Grid
                    data={sortedRealTime}
                    style={{ width: '100%' }}
                    scrollable="none"
                    resizable
                    sortable={{ allowUnsort: true, mode: 'multiple' }}
                    sort={sortRealTime}
                    onSortChange={e => setSortRealTime(e.sort)}
                >
                    <Column field="number" title="Real-Time Market Zone" />
                    <Column field="GenRegMCP" title="Generation Regulation" className="k-text-end" format="{0:n2}" />
                    <Column field="GenSpinMCP" title="Generation Spinning Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="GenSuppMCP" title="Generation Supplemental Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="StrMcp" title="Short-Term Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandRegMcp" title="Demand Regulation" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandSpinMcp" title="Demand Spinning Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="DemandSuppMCP" title="Demand Supplemental Reserve" className="k-text-end" format="{0:n2}" />
                    <Column field="RcpUpMcp" title="Ramp Capability Up" className="k-text-end" format="{0:n2}" />
                    <Column field="RcpDownMcp" title="Ramp Capability Down" className="k-text-end" format="{0:n2}" />
                </Grid>
            </div>
        </>
    );
}

